import {
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import CollapseIcon from "@mui/icons-material/KeyboardDoubleArrowLeftOutlined";
import ExpandIcon from "@mui/icons-material/KeyboardDoubleArrowRightOutlined";
import { NavItem, NAV_SECTIONS, isItemActive } from "./navigation";
import { CaseHeroLogo } from "../CaseHeroLogo";
import { ThemeToggle } from "./ThemeToggle";
import { UserMenu } from "./UserMenu";
import {
  ANIMATIONS,
  APPBAR_HEIGHT,
  NAV_ITEM_HEIGHT,
  SIDEBAR_GUTTER,
} from "../../styles/layout";

/** Uppercase group label. Collapses to a hairline rule on the mini rail. */
const SectionLabel = ({ title, open }: { title: string; open: boolean }) => {
  if (!open) {
    return (
      <Divider
        sx={{ mx: 1.5, my: 1.25 }}
        aria-label={title}
      />
    );
  }

  return (
    <Typography
      variant="overline"
      component="div"
      sx={{
        px: 1.5,
        pt: 2,
        pb: 0.75,
        color: "text.secondary",
        lineHeight: 1.4,
      }}
    >
      {title}
    </Typography>
  );
};

/** Leading slot: an icon when expanded, the case abbreviation otherwise. */
const ItemGlyph = ({
  item,
  active,
  open,
}: {
  item: NavItem;
  active: boolean;
  open: boolean;
}) => {
  const color = active ? "secondary.main" : "text.secondary";

  if (item.abbr && !open) {
    return (
      <Box
        sx={{
          width: 24,
          display: "flex",
          justifyContent: "center",
          fontSize: "0.6875rem",
          fontWeight: 700,
          letterSpacing: "0.02em",
          color,
        }}
      >
        {item.abbr}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 24,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color,
        transition: `color ${ANIMATIONS.fast}`,
        "& .MuiSvgIcon-root": { fontSize: "1.25rem" },
      }}
    >
      {item.icon}
    </Box>
  );
};

interface NavRowProps {
  item: NavItem;
  open: boolean;
}

const NavRow = ({ item, open }: NavRowProps) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const active = isItemActive(item, pathname);

  const row = (
    <ListItemButton
      onClick={() => navigate(item.path)}
      selected={active}
      disableRipple
      sx={(theme) => ({
        borderRadius: 2,
        minHeight: NAV_ITEM_HEIGHT,
        mb: 0.25,
        px: open ? 1.25 : 0,
        gap: open ? 1.25 : 0,
        justifyContent: open ? "flex-start" : "center",
        width: open ? "auto" : NAV_ITEM_HEIGHT,
        mx: open ? 0 : "auto",
        transition: `background-color ${ANIMATIONS.fast}`,
        "&:hover": {
          backgroundColor: theme.palette.surface.hover,
        },
        // One neutral selection everywhere; the green glyph and the
        // heavier label are what mark the active item.
        "&.Mui-selected": {
          backgroundColor: theme.palette.surface.selected,
          "&:hover": { backgroundColor: theme.palette.surface.selected },
        },
      })}
    >
      <ItemGlyph item={item} active={active} open={open} />

      {open && (
        <Typography
          component="span"
          sx={{
            fontSize: "0.875rem",
            fontWeight: active ? 600 : 500,
            color: active ? "text.primary" : "text.secondary",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item.title}
        </Typography>
      )}

      {open && item.abbr && (
        <Typography
          component="span"
          variant="caption"
          sx={{
            ml: "auto",
            color: "text.disabled",
            fontWeight: 600,
            letterSpacing: "0.02em",
          }}
        >
          {item.abbr}
        </Typography>
      )}
    </ListItemButton>
  );

  return open ? (
    row
  ) : (
    <Tooltip title={item.title} placement="right">
      {row}
    </Tooltip>
  );
};

/** Search affordance that opens the command palette. */
const SearchTrigger = ({
  open,
  onOpen,
}: {
  open: boolean;
  onOpen: () => void;
}) => {
  const trigger = (
    <Box
      component="button"
      type="button"
      onClick={onOpen}
      aria-label="Suchen"
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        gap: 1,
        width: open ? "100%" : NAV_ITEM_HEIGHT,
        height: NAV_ITEM_HEIGHT,
        mx: open ? 0 : "auto",
        px: open ? 1.25 : 0,
        justifyContent: open ? "flex-start" : "center",
        borderRadius: 8,
        cursor: "pointer",
        font: "inherit",
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        transition: `border-color ${ANIMATIONS.fast}, background-color ${ANIMATIONS.fast}`,
        "&:hover": {
          backgroundColor: theme.palette.surface.hover,
        },
      })}
    >
      <SearchIcon sx={{ fontSize: "1.125rem" }} />
      {open && (
        <>
          <Typography component="span" sx={{ fontSize: "0.875rem" }}>
            Suchen
          </Typography>
          <Box
            component="kbd"
            sx={(theme) => ({
              ml: "auto",
              display: "flex",
              alignItems: "center",
              gap: 0.25,
              px: 0.75,
              height: 22,
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.surface.inset,
              color: theme.palette.text.secondary,
              fontFamily: "inherit",
              fontSize: "0.6875rem",
              fontWeight: 600,
            })}
          >
            ⌘ K
          </Box>
        </>
      )}
    </Box>
  );

  return open ? (
    trigger
  ) : (
    <Tooltip title="Suchen (⌘K)" placement="right">
      {trigger}
    </Tooltip>
  );
};

/** Collapses the sidebar to the icon rail and back. */
const SidebarToggle = ({
  open,
  onToggle,
}: {
  open: boolean;
  onToggle: () => void;
}) => {
  const label = open ? "Menü einklappen" : "Menü ausklappen";

  return (
    <Tooltip title={label} placement="right">
      <IconButton
        onClick={onToggle}
        aria-label={label}
        aria-expanded={open}
        size="small"
        sx={{ borderRadius: 2 }}
      >
        {open ? (
          <CollapseIcon sx={{ fontSize: "1.25rem" }} />
        ) : (
          <ExpandIcon sx={{ fontSize: "1.25rem" }} />
        )}
      </IconButton>
    </Tooltip>
  );
};

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
  onOpenSearch: () => void;
}

export const Sidebar = ({ open, onToggle, onOpenSearch }: SidebarProps) => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Brand block - height-matched to the app bar so the two align */}
      <Box
        sx={{
          height: APPBAR_HEIGHT,
          minHeight: APPBAR_HEIGHT,
          display: "flex",
          alignItems: "center",
          gap: 1,
          justifyContent: open ? "flex-start" : "center",
          px: open ? `${SIDEBAR_GUTTER}px` : 0,
          flexShrink: 0,
        }}
      >
        <Tooltip title="Zum Dashboard" placement="right">
          <Box
            component="button"
            type="button"
            onClick={() => navigate("/")}
            aria-label="Zum Dashboard"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              p: 0.5,
              border: "none",
              borderRadius: 2,
              cursor: "pointer",
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: (theme) => theme.palette.surface.hover,
              },
            }}
          >
            <CaseHeroLogo open={open} />
          </Box>
        </Tooltip>

        {/* Expanded: the toggle sits beside the wordmark. Collapsed, the
            rail is too narrow for both, so it moves to its own row below. */}
        {open && (
          <Box sx={{ ml: "auto" }}>
            <SidebarToggle open={open} onToggle={onToggle} />
          </Box>
        )}
      </Box>

      {!open && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            pb: 1,
            flexShrink: 0,
          }}
        >
          <SidebarToggle open={open} onToggle={onToggle} />
        </Box>
      )}

      <Box sx={{ px: `${SIDEBAR_GUTTER}px`, pb: 1, flexShrink: 0 }}>
        <SearchTrigger open={open} onOpen={onOpenSearch} />
      </Box>

      {/* Scrollable navigation */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          px: `${SIDEBAR_GUTTER}px`,
          pb: 1,
        }}
      >
        {NAV_SECTIONS.map((section) => (
          <Box key={section.title ?? "root"}>
            {section.title && (
              <SectionLabel title={section.title} open={open} />
            )}
            <List disablePadding>
              {section.items.map((item) => (
                <NavRow key={item.path} item={item} open={open} />
              ))}
            </List>
          </Box>
        ))}
      </Box>

      {/* Footer - pinned, matching the reference */}
      <Box
        sx={{
          flexShrink: 0,
          px: `${SIDEBAR_GUTTER}px`,
          py: 1.25,
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <ThemeToggle open={open} />

        {/* Account sits last, at the very foot of the rail */}
        <Divider sx={{ my: 1 }} />
        <UserMenu open={open} />
      </Box>
    </Box>
  );
};

export default Sidebar;
